import React from 'react';
import PropTypes from 'prop-types';
import styled, {css} from 'styled-components';
import {colorBrandBase, formWhiteBackgroundColor, formLightBackgroundColor, formBoxShadow, boxShadowStandard} from '@nib-components/theme';
import {Box, Columns, Column, Stack, p} from '@nib/layout';
import Heading from '@nib-components/heading';
import {IconProps, InfoSystemIcon} from '@nib/icons';

export const validBackgroundColorValues = ['light', 'white'] as const;
export const validTipLocationValues = ['left', 'center', 'right'] as const;
export type backgroundColorValues = (typeof validBackgroundColorValues)[number];
export type tipLocationValues = (typeof validTipLocationValues)[number];

export interface InfoBoxProps {
  title?: string;
  titleComponent?: string;
  withIcon?: boolean;
  icon?: React.FC<IconProps>;
  withBorder?: boolean;
  iconAndBorderColor?: string;
  backgroundColor?: backgroundColorValues;
  withBoxShadow?: boolean;
  withTip?: boolean;
  tipLocation?: tipLocationValues;
  children: React.ReactNode;
  [key: string]: unknown; // other props
}

const getTipAlignment = (alignment: tipLocationValues): string => {
  if (alignment === 'right') {
    return `
      margin-left: auto;
      margin-right: 1rem;
    `;
  } else if (alignment === 'center') {
    return `
      margin-left: auto;
      margin-right: auto;
    `;
  }

  return `margin-left: 2rem`;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const InfoBoxContainer = styled(({withBorder, borderColor, backgroundColor, withBoxShadow, theme, ...rest}) => <Box {...rest} />)`
  position: relative;
  overflow: hidden;
  ${p(5)};

  // Additional .25rem for the absolutely positioned border below.
  // We apply this even when no border is rendered because the text indentation should be consistent
  // across InfoBoxes with and without the left border.
  padding-left: 1.75rem;

  ${({withBorder, borderColor}) =>
    withBorder &&
    css`
      ::before {
        content: '';
        display: block;
        width: 0.25rem;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        background-color: ${borderColor ? borderColor : colorBrandBase};
      }
    `}

  ${({withBoxShadow}) =>
    withBoxShadow &&
    css`
      box-shadow: ${boxShadowStandard || formBoxShadow};
    `}

  ${({backgroundColor}) =>
    backgroundColor &&
    css`
      background-color: ${backgroundColor === 'light'
        ? css`
            ${formLightBackgroundColor}
          `
        : css`
            ${formWhiteBackgroundColor}
          `};
    `}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const InfoBoxTip = styled(({backgroundColor, tipLocation, ...rest}) => <div {...rest} />)`
  position: relative;
  z-index: 1;
  width: 24px;
  height: 15px;
  ${({tipLocation}) => tipLocation && getTipAlignment(tipLocation)};

  &::after {
    content: '';
    display: block;
    position: absolute;
    bottom: 0;
    border-left: 12px solid transparent;
    border-right: 12px solid transparent;
    border-top: 15px solid transparent;
    border-bottom: 15px solid ${props => props.backgroundColor && (props.backgroundColor === 'light' ? formLightBackgroundColor : formWhiteBackgroundColor)};
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const IconColumn = styled(({isSystemIcon, ...rest}) => <Column {...rest} />)`
  align-self: flex-start;
  font-size: 0;
  line-height: 1;

  // If iconAndBorderColor is provided then this does nothing.
  ${({isSystemIcon}) =>
    isSystemIcon &&
    css`
      color: ${colorBrandBase};
    `}
`;

// A box around the icon to ensure system icons occupy as much space as graphic ones
const FixedWidthBox: React.FC = (props: any) => <Box width="2rem" display="flex" justifyContent="center" {...props} />;

const InfoBox: React.FC<InfoBoxProps> = props => {
  const {children, title, titleComponent, withTip, tipLocation, backgroundColor, withBorder, iconAndBorderColor, withBoxShadow, withIcon, icon: Icon, ...otherProps} = props;
  const isSystemIcon = Icon && Icon.displayName?.includes('System');
  return (
    <div>
      {withTip && <InfoBoxTip tipLocation={tipLocation} backgroundColor={backgroundColor} />}
      <InfoBoxContainer backgroundColor={backgroundColor} withBorder={withBorder} borderColor={iconAndBorderColor} withBoxShadow={withBoxShadow} borderRadius="standard" {...otherProps}>
        <Columns verticalAlign="center" space={5}>
          {withIcon && (
            <IconColumn width="content" isSystemIcon={isSystemIcon}>
              {Icon && (
                <FixedWidthBox>
                  <Icon size={isSystemIcon ? 'sm' : 'md'} fill={iconAndBorderColor} />
                </FixedWidthBox>
              )}
            </IconColumn>
          )}
          <Column>
            <Stack space={2}>
              {title && (
                <Heading size={4} component={titleComponent}>
                  {title}
                </Heading>
              )}
              <div>{children}</div>
            </Stack>
          </Column>
        </Columns>
      </InfoBoxContainer>
    </div>
  );
};

InfoBox.displayName = 'InfoBox';

InfoBox.propTypes = {
  title: PropTypes.string,
  titleComponent: PropTypes.string,
  withIcon: PropTypes.bool,
  icon: PropTypes.func,
  withBorder: PropTypes.bool,
  iconAndBorderColor: PropTypes.string,
  backgroundColor: PropTypes.oneOf(validBackgroundColorValues),
  withBoxShadow: PropTypes.bool,
  withTip: PropTypes.bool,
  tipLocation: PropTypes.oneOf(validTipLocationValues),
  children: PropTypes.node.isRequired
};

InfoBox.defaultProps = {
  titleComponent: 'h3',
  withIcon: true,
  icon: InfoSystemIcon,
  backgroundColor: 'white',
  withBorder: true,
  withBoxShadow: true,
  withTip: false,
  tipLocation: 'left'
};

export default InfoBox;
